import React from 'react';
import * as api from '../../../lib/Api';
import {
  Type,
  Image,
  IconArrowTopRight,
  Tooltip,
  TooltipTrigger,
  ButtonIcon,
  IconHelpAlt,
} from '@spotify-internal/encore-creator-web';
import styled from 'styled-components';
import styles from './index.module.scss';
import { TopArtist } from '../../../types';
import colors from '@spotify-internal/encore-foundation';
import { FormattedMessage } from 'react-intl';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 68px;
  grid-row-gap: 0px;
  margin-top: 31px;
`;

const Avatar = styled(Image)`
  display: block;
  margin-bottom: 14px;
`;

const InlineIconArrowTopRight = styled(IconArrowTopRight)`
  display: none;
  color: ${colors['spotify-green']};
  position: absolute;
  right: -20px;
  top: 4px;
`;

const Artist = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  &:hover ${InlineIconArrowTopRight} {
    display: inline-block;
  }
`;

const ArtistText = styled(Type.p)`
  position: relative;
  text-align: center;
`;

const ArtistLink = styled.a`
  text-decoration: none;
  :hover {
    color: #000;
  }
`;

const ChartLabel = styled(Type.h1)`
  display: flex;
  align-items: flex-start;
`;

interface Props {
  showId: string;
}

export function TopArtistsChart(props: Props) {
  const [artists, setArtists] = React.useState<TopArtist[]>([]);
  const [showTooltip, setShowTooltip] = React.useState(false);

  React.useEffect(() => {
    let didCancel = false;

    async function fetchData() {
      const artists = await api.getTopArtistsForPodcast(props.showId);

      /* istanbul ignore next */
      if (didCancel) {
        return;
      }

      setArtists(artists);
    }

    fetchData();

    return /* istanbul ignore next */ () => {
      didCancel = true;
    };
  }, [props.showId]);

  if (artists.length === 0) {
    return null;
  }

  function getArtistIdFromUri(artistUri: String) {
    return artistUri.replace(`spotify:artist:`, ``);
  }

  return (
    <div className={styles.chart}>
      <ChartLabel variant={Type.heading3}>
        <FormattedMessage
          id="podcast.artists-listening.title"
          defaultMessage="Artists they’re listening to"
        />
        <TooltipTrigger
          overlay={
            /* istanbul ignore next */ showTooltip && (
              <Tooltip>
                <FormattedMessage
                  id="podcast.artists-listening.tooltip"
                  defaultMessage="Your podcast listeners have streamed these artists the most in the last 28 days."
                />
              </Tooltip>
            )
          }
          placement="bottom"
          onShow={/* istanbul ignore next */ () => setShowTooltip(true)}
          onHide={/* istanbul ignore next */ () => setShowTooltip(false)}
        >
          <ButtonIcon style={{ marginLeft: '5px', marginTop: '5px' }}>
            <IconHelpAlt iconSize={16} />
          </ButtonIcon>
        </TooltipTrigger>
      </ChartLabel>
      <Grid>
        {artists.map(artist => {
          return (
            <ArtistLink
              href={`https://open.spotify.com/artist/${getArtistIdFromUri(artist.uri)}`}
              key={artist.uri}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Artist>
                <Avatar
                  src={artist.image}
                  alt={artist.title}
                  imageHeight="80px"
                  imageWidth="80px"
                  crop
                  circle
                />
                <ArtistText variant={Type.body1} weight={Type.book}>
                  {artist.title}
                  <InlineIconArrowTopRight iconSize={16} />
                </ArtistText>
              </Artist>
            </ArtistLink>
          );
        })}
      </Grid>
    </div>
  );
}
